<script setup lang="ts">
  import { BaseSelect } from '@qic/ui-components'
  import { computed, onMounted, onUnmounted, ref } from 'vue'
  import CrossIcon from '@/shared/components/icons/CrossIcon.vue'
  import { useBodyScroll } from '@/shared/composables'

  interface Props {
    modelValue: string
    label: string
    dataTest?: string
  }

  interface Emits {
    (e: 'update:modelValue', data: string): void
  }

  const props = withDefaults(defineProps<Props>(), {
    dataTest: 'new-base-select',
  })
  const emit = defineEmits<Emits>()

  const { allowBodyScroll, disableBodyScroll } = useBodyScroll()
  const isDropdownActive = ref(false)
  const mediaQuery = window.matchMedia(`(max-width: 550px)`)
  const isMobile = ref(mediaQuery.matches)
  const handleResize = (event: MediaQueryListEvent) => {
    isMobile.value = event.matches
  }

  const onDropdownOpen = () => {
    isDropdownActive.value = true

    if (isMobile.value) {
      disableBodyScroll()
    }
  }

  const onDropdownClose = () => {
    isDropdownActive.value = false
    allowBodyScroll()
  }

  onMounted(async () => {
    mediaQuery.addEventListener('change', handleResize)
  })

  onUnmounted(() => {
    mediaQuery.removeEventListener('change', handleResize)
  })

  const selectedOption = computed({
    get: () => props.modelValue,
    set: (newValue) => emit('update:modelValue', newValue),
  })
</script>

<template>
  <teleport to="body" :disabled="!(isMobile && isDropdownActive)">
    <div class="flow-travel-select" :class="{ 'flow-travel-select--active': isDropdownActive }">
      <div class="flow-travel-select__header">
        <h1>{{ label }}</h1>
        <CrossIcon color="#526C82CC" @click="onDropdownClose" />
      </div>
      <BaseSelect
        v-model="selectedOption"
        class="flow-travel-select__dropdown"
        :label="label"
        v-bind="$attrs"
        :data-test="dataTest"
        @open="onDropdownOpen"
        @close="onDropdownClose"
      >
        <template #option="option">
          <slot name="option" v-bind="option" />
        </template>
      </BaseSelect>
    </div>
  </teleport>
</template>

<style lang="scss">
  @import '@/shared/assets/styles/utils';

  .base-select-control__input {
    font-size: 16px;
  }

  .flow-travel-select__header {
    display: none;
  }

  .flow-travel-select__dropdown {
    .base-select__option {
      display: flex;
      align-items: center;
    }
  }

  @include mobile-flow {
    .flow-travel-select--active {
      padding: 16px;
      z-index: 999;

      .flow-travel-select__dropdown {
        .base-select-options {
          border-radius: 0;
          border: none;
          max-height: calc(100svh - (16px + 16px + 80px + 80px)) !important;
          box-shadow: none;
          position: static;
        }
      }

      &.flow-travel-select {
        position: fixed;
        height: 100svh;
        width: 100vw;
        top: 0;
        background-color: #fff;
      }

      .flow-travel-select__header {
        display: flex;
        padding-block-end: 16px;
        max-width: 416px;
        margin: 0 auto;
        align-items: center;
        justify-content: space-between;
      }
    }
  }
</style>
